import api from "./client";
import type { TipoSeparador, HorarioSeparador } from "./configuracao";


// ==================== TAREFAS ====================

export interface CreateTarefaDto {
	acao:
		| "separacao"
		| "reposicao"
		| "reposicao de pedido"
		| "separacao complementar"
		| "nota entrada";
	status?:
		| "pendente"
		| "aguardando"
		| "em andamento"
		| "em transporte"
		| "concluida"
		| "cancelada";
	usuario_id?: number;
	pedido_ids?: number[];
	reposicao_ids?: number[];
	mesa_separacao_id?: number;
}


export interface TarefaResponse {
	tarefa_id: number;
	acao: string;
	status: string;
	data_hora_enviado: string;
	data_hora_iniciado?: string;
	data_hora_encerrado?: string;
	usuario?: {
		usuario_id: number;
		usuario: string;
		responsavel: string;
	};
	pedido?: Array<{
		pedido_id: number;
		num_pedido: string;
		situacao: number;
		data: string;
		valor: string;
		canal_venda: string;
		num_pedido_ecommerce: string;
		cliente?: {
			nome: string;
		};
	}>;
	reposicao?: Array<any>;
	produto_tarefa?: Array<any>;
}


export interface GerarTarefasPedidosManualPayload {
	pedido_ids: number[];
	mesa_separacao_id: number;
}


export interface GerarTarefasPedidosManualResponse {
	tarefas_separacao: Array<{ tarefa_id: number }>;
	tarefas_reposicao_pedido: Array<{ tarefa_id: number }>;
	pedidos_para_separacao: number[];
	pedidos_para_reposicao: number[];
	pedidos_aguardando_reposicao_em_andamento: number[];
	avisos: string[];
}


export const criarTarefa = async (
	dados: CreateTarefaDto,
): Promise<TarefaResponse | TarefaResponse[]> => {
	try {
		const res = await api.post("/tarefa", dados);
		return res.data;
	} catch (error: any) {
		// Extrair a mensagem de erro do backend
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao criar tarefa. Tente novamente.");
	}
};


export const gerarTarefasPedidosManual = async (
	dados: GerarTarefasPedidosManualPayload,
): Promise<GerarTarefasPedidosManualResponse> => {
	try {
		const res = await api.post("/tarefa/gerar-pedidos", dados);
		return res.data;
	} catch (error: any) {
		// #1477: 409 é o portão da geração, não um erro de seleção. Marcado para
		// a tela poder dar o tratamento certo em vez de sugerir filtrar canal.
		if (error.response?.status === 409) {
			const bloqueio = new Error(
				error.response?.data?.message ??
					"A geração está travada por tarefas de hoje ainda não iniciadas.",
			);
			(bloqueio as any).portaoBloqueado = true;
			throw bloqueio;
		}
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao gerar tarefas para pedidos selecionados.");
	}
};


// ==================== PORTÃO DE GERAÇÃO (#1477) ====================

export interface TarefaSegurandoPortao {
	tarefa_id: number;
	acao: string;
	status: string;
	data_limite_despacho: string | null;
}

export interface StatusPortaoGeracao {
	/** A geração de pedidos sem prazo pode rodar? */
	liberado: boolean;
	/** Frase pronta para exibir, já nomeando as tarefas. `null` quando liberado. */
	motivo: string | null;
	total: number;
	tarefas: TarefaSegurandoPortao[];
}

/**
 * #1477 — pergunta ao backend se a 2ª leva da geração está liberada.
 *
 * A 1ª leva (pedido com despacho para hoje ou atrasado) nunca é travada, então
 * seleção que contenha um pedido desses passa mesmo com o portão fechado.
 */
export const buscarStatusPortaoGeracao =
	async (): Promise<StatusPortaoGeracao> => {
		const res = await api.get("/tarefa/portao-geracao");
		return res.data;
	};

export interface PedidosAguardandoGeracao {
	/** `false` quando a regra das levas está desligada — nada aguarda por ela. */
	ativo: boolean;
	total: number;
	/** Esperando o dia do prazo chegar. Normal. */
	prazo_futuro: number;
	/** Sem prazo, em canal fora da 2ª leva. */
	sem_prazo_bloqueado: number;
	/** Subconjunto do anterior, em canal do Mercado Livre — o caso de alarme. */
	sem_prazo_bloqueado_ml: number;
}

/**
 * #1477 — quantos pedidos estão parados por causa da regra das levas.
 *
 * O número que importa é `sem_prazo_bloqueado_ml`: pedido do Mercado Livre cujo
 * prazo ainda não chegou da API do Gestão. Ele não entra na 1ª leva (não tem
 * prazo) nem na 2ª (canal não habilitado) e ficaria parado em silêncio.
 */
export const buscarPedidosAguardandoGeracao =
	async (): Promise<PedidosAguardandoGeracao> => {
		const res = await api.get("/tarefa/pedidos-aguardando-geracao");
		return res.data;
	};


// Verificar se usuário tem tarefas atribuídas (exceto concluídas e canceladas)
export const verificarTarefasAtribuidasUsuario = async (
	usuarioId: number,
): Promise<{ temTarefas: boolean; quantidade: number }> => {
	try {
		const response = await api.get(
			`/tarefa/usuario/${usuarioId}/verificar-tarefas`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao verificar tarefas do usuário:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao verificar tarefas do usuário.");
	}
};


// Reatribuir tarefas de um usuário para outros operadores
export const reatribuirTarefasUsuario = async (
	usuarioId: number,
): Promise<{
	message: string;
	quantidadeReatribuida: number;
	quantidadeSemOperadores: number;
	detalhes: Array<{
		tarefa_id: number;
		usuario_id: number | null;
		motivo: string;
	}>;
}> => {
	try {
		const response = await api.post(
			`/tarefa/usuario/${usuarioId}/reatribuir-tarefas`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao reatribuir tarefas do usuário:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao reatribuir tarefas do usuário.");
	}
};


// Buscar operadores disponíveis em um determinado dia e horário
export const buscarSeparadoresDisponiveis = async (
	tipoSeparador: TipoSeparador,
	diaDaSemana: number,
	horario?: string,
): Promise<HorarioSeparador[]> => {
	try {
		const params: any = {
			tipo_separador: tipoSeparador,
			dia_da_semana: diaDaSemana,
		};
		if (horario) params.horario = horario;

		const response = await api.get("/horario-separador/disponiveis", {
			params,
		});
		return response.data;
	} catch (error) {
		console.error("Erro ao buscar separadores disponíveis:", error);
		throw error;
	}
};

